<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardLinkSections label="Solid Waste">
                <LandingCardLinkSection
                  link-label="Find Facilities"
                  description="Find, view, and manage solid waste facilities."
                  :to="{ name: routeNames.facilitySite.facilitySites }"
                />
                <LandingCardLinkSection
                  link-label="Find Permits"
                  description="Find, view, and manage facility permits."
                  :to="{ name: routeNames.permit.permits }"
                />
                <LandingCardLinkSection
                  link-label="Find Disposal Reports"
                  description="Find, view, and manage facility disposal reports."
                  :to="{ name: routeNames.disposalReport.disposalReports }"
                />
                <LandingCardLinkSection
                  link-label="Find Contacts"
                  description="Find, view, and manage facility contacts."
                  :to="{ name: routeNames.person.persons }"
                />
                <LandingCardLinkSection
                  link-label="Find Inspections"
                  description="Find, view, and manage facility inspections."
                  :to="{ name: routeNames.inspection.inspections }"
                />
              </LandingCardLinkSections>
              <LandingCardLinkSections label="Sustainability">
                <LandingCardLinkSection
                  link-label="Find Organizations"
                  description="Find, view, and manage recycling material generators, state agencies, and waste haulers and/or processors."
                  :to="{ name: routeNames.organization.organizations }"
                />
                <LandingCardLinkSection
                  link-label="Find Recycling Reports"
                  description="Find, view, and manage organization recycling reports."
                  :to="{ name: routeNames.recyclingReport.recyclingReports }"
                />
                <LandingCardLinkSection
                  link-label="Find SWMP"
                  description="Find and view county solid waste management plans."
                  :to="{ name: routeNames.swmp.swmps }"
                />
              </LandingCardLinkSections>
            </template>
          </LandingCard>
          <LandingCard title="Analytics">
            <template #default>
              <div class="py-5 pl-4 sm:pl-6">
                <PublicLandingList>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.exportFacilityData,
                      }"
                    >
                      Export Facility Data</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.generatedWasteReport,
                      }"
                    >
                      Run Generated Waste Report</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.exportDisposalReportData,
                      }"
                    >
                      Export Disposal Report Data</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.organizationMaterialReport,
                      }"
                    >
                      Run Organization Material Report</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.recycledMaterialReport,
                      }"
                    >
                      Run Recycled Material Report</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.recyclingRateReport,
                      }"
                    >
                      Run Recycling Rate Report</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.exportRecyclingReportData,
                      }"
                    >
                      Export Recycling Report Data</router-link
                    >
                  </PublicLandingListItem>
                  <PublicLandingListItem>
                    <router-link
                      class="text-sky-600 hover:underline"
                      :to="{
                        name: routeNames.analytics.emailList,
                      }"
                    >
                      Run Email List Report</router-link
                    >
                  </PublicLandingListItem>
                </PublicLandingList>
              </div>
            </template>
          </LandingCard>
        </div>
        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-2">
            <LandingCard
              title="Disposal Reports"
              :view-all-to="{ name: routeNames.disposalReport.disposalReports, query: { statusMurnIn: [] } }"
            >
              <LandingCardSkeleton v-if="areDisposalReportsLoading" />
              <div
                class="bg-neutral-50 p-10 sm:rounded-b-lg"
                v-if="
                  !areDisposalReportsLoading && !pastDueDisposalReports?.length && !submittedDisposalReports?.length
                "
              >
                <NoneFoundFlag label="No Past Due or Submitted Disposal Reports" />
              </div>

              <LandingCardEntityList
                title="Past Due &ndash; 5 oldest"
                :items="submittedDisposalReports"
                variant="warn"
                :view-all-to="{
                  name: routeNames.disposalReport.disposalReports,
                  query: { statusMurnIn: [LE.DisposalReportStatus.PastDue] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      {{ item.year }}
                      <LookupDescription :murn="item.periodMurn" />
                      <SingleSpace />
                      <LookupDescription :murn="item.typeMurn" />
                    </template>
                    <template #secondaryLabel>
                      {{ item.facilitySite?.name }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.dueDate"
                      >
                        <span class="md:block"
                          >Due
                          <span class="text-neutral-500">{{
                            // item.id
                            sysDate.shortDateString(item.dueDate)
                          }}</span></span
                        >
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
              <LandingCardEntityList
                title="Submitted &ndash; 5 newest"
                :items="submittedDisposalReports"
                :view-all-to="{
                  name: routeNames.disposalReport.disposalReports,
                  query: { statusMurnIn: [LE.DisposalReportStatus.Submitted] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      {{ item.year }}
                      <LookupDescription :murn="item.periodMurn" />
                      <SingleSpace />
                      <LookupDescription :murn="item.typeMurn" />
                    </template>
                    <template #secondaryLabel>
                      {{ item.facilitySite?.name }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.submittedDateTime"
                      >
                        <span class="md:block"
                          >Received
                          <span class="text-neutral-500">{{
                            // item.id
                            sysDate.shortDateString(item.receivedDate)
                          }}</span></span
                        >
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
            </LandingCard>
          </div>

          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-3">
            <LandingCard
              title="Recycling Reports"
              :view-all-to="{ name: routeNames.recyclingReport.recyclingReports, query: { statusMurnIn: [] } }"
            >
              <LandingCardSkeleton v-if="areRecyclingReportsLoading" />
              <div
                class="bg-neutral-50 p-10 sm:rounded-b-lg"
                v-if="!areRecyclingReportsLoading && !submittedRecyclingReports?.length"
              >
                <NoneFoundFlag label="No Submitted Recycling Reports" />
              </div>

              <LandingCardEntityList
                title="Submitted &ndash; 5 newest"
                :items="submittedRecyclingReports"
                :view-all-to="{
                  name: routeNames.recyclingReport.recyclingReports,
                  query: { statusMurnIn: [LE.RecyclingReportStatus.Submitted] },
                }"
              >
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      {{ item.year }}
                      <LookupDescription :murn="item.frequencyMurn" />
                      <SingleSpace />
                      <LookupDescription :murn="item.typeMurn" />
                    </template>
                    <template #secondaryLabel>
                      {{ item.organization?.name }}
                    </template>
                    <template #additionalInfo>
                      <div
                        class="mt-1.5 text-sm text-neutral-400 md:mt-0 md:self-center md:pr-4 md:text-right"
                        v-if="item.receivedDate"
                      >
                        <span class="md:block"
                          >Received
                          <span class="text-neutral-500">{{ sysDate.shortDateString(item.receivedDate) }}</span></span
                        >
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
            </LandingCard>
          </div>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enumsNew";
import { routeNames } from "@/routes";
import { getDisposalReports, getRecyclingReports } from "@/services/endpoints";
import type { DisposalReport } from "@/views/disposalReport/disposalReport";
import disposalReportService from "@/views/disposalReport/disposalReportService";
import type { RecyclingReport } from "@/views/recyclingReport/recyclingReport";
import recyclingReportService from "@/views/recyclingReport/recyclingReportService";
import { BaseButton } from "@modernary/mui/components/button";
import { NoneFoundFlag, SingleSpace, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import { useToastStore } from "@modernary/mui/components/toasts";
import type { Paging } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { sysDate, tzDate } from "@modernary/mui/utils/filters";
import { AxiosError } from "axios";
import { defineComponent, ref } from "vue";
import type { LandingCardEntity } from "./landing/landingCard";

export default defineComponent({
  name: "HomeInternal",
  components: {
    LandingBackground,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    NoneFoundFlag,
    LookupDescription,
    SingleSpace,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();

    const toastStore = useToastStore();
    const areDisposalReportsLoading = ref(2);
    const areRecyclingReportsLoading = ref(1);

    const pastDueDisposalReports = ref<LandingCardEntity<DisposalReport>[]>();
    getDisposalReports({
      statusMurnIn: [LE.DisposalReportStatus.PastDue],
      orderBy: [{ propertyName: "dueDate", isDescending: false }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          disposalReportService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<DisposalReport>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.disposalReport.view, params: { id: item.id } };
        });
        pastDueDisposalReports.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Disposal Reports";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areDisposalReportsLoading.value--;
      });

    const submittedDisposalReports = ref<LandingCardEntity<DisposalReport>[]>();
    getDisposalReports({
      statusMurnIn: [LE.DisposalReportStatus.Submitted],
      orderBy: [{ propertyName: "receivedDate", isDescending: false }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          disposalReportService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<DisposalReport>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.disposalReport.view, params: { id: item.id } };
        });
        submittedDisposalReports.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Disposal Reports";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areDisposalReportsLoading.value--;
      });

    const submittedRecyclingReports = ref<LandingCardEntity<RecyclingReport>[]>();
    getRecyclingReports({
      statusMurnIn: [LE.RecyclingReportStatus.Submitted],
      orderBy: [{ propertyName: "receivedDate", isDescending: false }],
      take: 5,
    })
      .then(async (response) => {
        const data = (await adaptPagedResponse(
          response,
          recyclingReportService.responseAdapter as any,
        )) as unknown as Paging<LandingCardEntity<RecyclingReport>>;
        data._items.forEach((item) => {
          item.to = { name: routeNames.recyclingReport.view, params: { id: item.id } };
        });
        submittedRecyclingReports.value = data._items;
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          toastStore.toastError((options) => {
            options.title = "Could not get Recycling Reports";
          }, error.status || 500);
        }
      })
      .finally(() => {
        areRecyclingReportsLoading.value--;
      });

    return {
      userRef,
      routeNames,
      BaseButton,
      LE,
      pastDueDisposalReports,
      areDisposalReportsLoading,
      sysDate,
      submittedDisposalReports,
      areRecyclingReportsLoading,
      submittedRecyclingReports,
    };
  },
});
</script>
